import { PlanType, RealEstateId, Vertical } from 'enums';
import { AppStore } from 'stores/App.store';
import { ParsedAppIntentElements } from 'types';

const pageTitleKeys = {
  CHANGE_PLAN: 'change_plan.app_title',
  PLANS: {
    PLANS: 'plans_page.title',
    PLANS_SECOND_UPGRADE: 'plans_page.second_upgrade_title',
    BOOKINGS: 'plans_page.booking_title',
    ACCEPT_PAYMENTS: 'plans_page.payments_title',
    BUSINESS: 'plans_page.business_title',
    ENTERPRISE: 'enterprise_plans.page.title',
    DOMAIN_INTENT: 'plans_page.domain_intent_title',
    SPECIAL_DISCOUNT: 'Advanced_Plans_special_discount_title',
    FITNESS: 'plans_page_fitness_title',
    BLOG: 'plans_page.blogs_title',
  },
  SUBSCRIPTIONS: 'subscription_page.advanced_plans_title',
  CHECKOUT: 'checkout.title',
};

const pageSubtitleMap = {
  DEFAULT: 'app.subtitleText',
  SECOND_UPGRADE: 'app.second_upgrade_text',
  CHANGE_PLAN: 'change_plan.app_sub_title',
  ENTERPRISE: 'enterprise_plans.page.subtitle',
  SPECIAL_DISCOUNT: {
    0: 'Advanced_Plans_plans_page_special_discount_subtitle',
    1: 'Advanced_Plans_subscription_page_special_discount_subtitle',
    2: 'Advanced_Plans_checkout_page_special_discount_subtitle',
  },
};

const getPlansPageTitle = (appStore): string => {
  const {
    planType,
    isBusinessVertical,
    appContext,
    isSecondUpgrade,
    isEditorX,
  } = appStore;
  const PlansPage = pageTitleKeys.PLANS;
  const vertical = appContext.verticalName;
  // editorx uses the second upgrade title for website and business
  // but in default flow website is getting the default title.
  if (isSecondUpgrade && isEditorX) {
    return PlansPage.PLANS_SECOND_UPGRADE;
  }
  if (planType === PlanType.Website) {
    return PlansPage.PLANS;
  }
  if (planType === PlanType.Enterprise) {
    return PlansPage.ENTERPRISE;
  }
  if (vertical === Vertical.Bookings) {
    return PlansPage.BOOKINGS;
  }
  if (isBusinessVertical) {
    return PlansPage.ACCEPT_PAYMENTS;
  }
  if (isSecondUpgrade) {
    return PlansPage.PLANS_SECOND_UPGRADE;
  }
  return PlansPage.BUSINESS;
};

const getProductPageTitleFromDealerOffers = (appStore): string => {
  const { isSecondUpgrade, planType, appContext } = appStore;
  const { parsedDealerOffers } = appContext;

  const appTitleByTab = (isSecondUpgrade
    ? parsedDealerOffers.get(RealEstateId.AppIntentElementsSecondUpgrade)
    : parsedDealerOffers.get(
        RealEstateId.AppIntentElementsInitialPurchase,
      )) as ParsedAppIntentElements;

  return appTitleByTab[planType];
};

interface AppTitles {
  appTitle: string;
  appSubtitle: string;
}

export const useAppTitles = (appStore: AppStore): AppTitles => {
  const {
    currentStep,
    isChangePlan,
    isSecondUpgrade,
    planType,
    isUseDealerInegrationIntentElementsEnabled,
    isEditorXSpecialDiscountEnabled,
  } = appStore;

  const getAppTitle = () => {
    if (isChangePlan) {
      return pageTitleKeys.CHANGE_PLAN;
    }

    if (currentStep === 0) {
      if (isEditorXSpecialDiscountEnabled) {
        return pageTitleKeys.PLANS.SPECIAL_DISCOUNT;
      }

      if (isUseDealerInegrationIntentElementsEnabled) {
        return getProductPageTitleFromDealerOffers(appStore);
      }
      return getPlansPageTitle(appStore);
    }

    if (currentStep === 1) {
      return pageTitleKeys.SUBSCRIPTIONS;
    }

    return pageTitleKeys.CHECKOUT;
  };

  const getAppSubtitle = () => {
    if (isEditorXSpecialDiscountEnabled) {
      return pageSubtitleMap.SPECIAL_DISCOUNT[currentStep];
    }
    if (isSecondUpgrade) {
      return pageSubtitleMap.SECOND_UPGRADE;
    }
    if (isChangePlan) {
      return pageSubtitleMap.CHANGE_PLAN;
    }
    if (planType === PlanType.Enterprise) {
      return pageSubtitleMap.ENTERPRISE;
    }
    return pageSubtitleMap.DEFAULT;
  };

  return {
    appTitle: getAppTitle(),
    appSubtitle: getAppSubtitle(),
  };
};
